import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PackagePlus, SlidersHorizontal, Trash2, Eraser, MessageCircle } from "lucide-react";
import { api, apiError, fmtDate, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const ROLL_CATS = ["PPF", "Sunfilm"];
const REASONS = [
  ["opening_stock_correction", "Opening stock correction"],
  ["damage", "Damage"],
  ["loss", "Loss"],
  ["count_correction", "Count correction"],
  ["other", "Other"],
];
const today = () => new Date().toISOString().slice(0, 10);

export function StockAdminTools({ defaultStudioId, onChanged }) {
  const [studios, setStudios] = useState([]);
  const [catalog, setCatalog] = useState([]);

  // opening stock
  const [openOpening, setOpenOpening] = useState(false);
  const [op, setOp] = useState({
    studio_id: defaultStudioId && defaultStudioId !== "all" ? defaultStudioId : "",
    catalog_sku_id: "", quantity: "", roll_mode: "full", remaining_sqft: "",
    capacity_sqft: "", serial: "", date: today(), unit_cost: "",
  });

  // adjustment
  const [openAdjust, setOpenAdjust] = useState(false);
  const [adj, setAdj] = useState({
    studio_id: defaultStudioId && defaultStudioId !== "all" ? defaultStudioId : "",
    catalog_sku_id: "", mode: "quantity", direction: "add", magnitude: "",
    serial_id: "", actual_remaining_sqft: "", reason: "", note: "",
  });
  const [serials, setSerials] = useState([]);

  // cleanup
  const [openCleanup, setOpenCleanup] = useState(false);
  const [orders, setOrders] = useState([]);
  const [delOrder, setDelOrder] = useState(null);

  useEffect(() => {
    api.get("/studios").then(({ data }) => setStudios(data)).catch(() => {});
    api.get("/catalog").then(({ data }) => setCatalog(data || [])).catch(() => {});
  }, []);

  const opSku = useMemo(() => catalog.find((c) => c.id === op.catalog_sku_id), [catalog, op.catalog_sku_id]);
  const opIsRoll = opSku && ROLL_CATS.includes(opSku.category);
  const adjSku = useMemo(() => catalog.find((c) => c.id === adj.catalog_sku_id), [catalog, adj.catalog_sku_id]);

  // load serials for the chosen studio+product when doing a roll correction
  useEffect(() => {
    if (openAdjust && adj.mode === "roll" && adj.studio_id) {
      api.get("/roll-serials", { params: { studio_id: adj.studio_id, has_balance: false } })
        .then(({ data }) => setSerials(data || [])).catch(() => setSerials([]));
    }
  }, [openAdjust, adj.mode, adj.studio_id]);

  const submitOpening = async () => {
    try {
      const payload = {
        catalog_sku_id: op.catalog_sku_id, studio_id: op.studio_id, date: op.date,
        unit_cost: op.unit_cost === "" ? null : Number(op.unit_cost),
      };
      if (opIsRoll) {
        payload.is_full_roll = op.roll_mode === "full";
        if (op.roll_mode === "partial") payload.remaining_sqft = Number(op.remaining_sqft || 0);
        if (op.capacity_sqft !== "") payload.capacity_sqft = Number(op.capacity_sqft);
      } else {
        payload.quantity = Number(op.quantity || 0);
      }
      await api.post("/stock/opening", payload);
      toast.success("Opening stock recorded — serial number generated");
      setOp((s) => ({ ...s, catalog_sku_id: "", quantity: "", remaining_sqft: "", capacity_sqft: "", serial: "" }));
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const sendSerialsWhatsApp = async (studioId) => {
    if (!studioId || studioId === "all") {
      toast.error("Pick a specific studio first");
      return;
    }
    try {
      const { data } = await api.get("/stock/serials/whatsapp", { params: { studio_id: studioId } });
      if (!data.phone) {
        toast.error("No studio-owner mobile on file for this studio");
        return;
      }
      const digits = String(data.phone).replace(/[^0-9]/g, "");
      const phone = digits.length === 10 ? `91${digits}` : digits;
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(data.text)}`, "_blank");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };


  const submitAdjust = async () => {
    try {
      const payload = {
        studio_id: adj.studio_id, catalog_sku_id: adj.catalog_sku_id,
        reason: adj.reason, note: adj.note,
      };
      if (adj.mode === "roll") {
        payload.serial_id = adj.serial_id;
        payload.actual_remaining_sqft = Number(adj.actual_remaining_sqft || 0);
      } else {
        const mag = Math.abs(Number(adj.magnitude || 0));
        payload.delta = adj.direction === "remove" ? -mag : mag;
      }
      await api.post("/stock/adjust", payload);
      toast.success("Stock adjusted");
      setOpenAdjust(false);
      setAdj((s) => ({ ...s, catalog_sku_id: "", magnitude: "", serial_id: "", actual_remaining_sqft: "", note: "" }));
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const loadOrders = async () => {
    try {
      const { data } = await api.get("/stock/backdated-orders");
      setOrders(data || []);
      setOpenCleanup(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const confirmReverse = async () => {
    try {
      await api.delete(`/material-orders/${delOrder.id}/reverse`);
      toast.success("Order deleted, stock reversed");
      setDelOrder(null);
      setOrders((o) => o.filter((x) => x.id !== delOrder.id));
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const studioSelect = (value, onChange, testid) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Select studio" /></SelectTrigger>
      <SelectContent>
        {studios.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const skuSelect = (value, onChange, testid) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Select product" /></SelectTrigger>
      <SelectContent>
        {catalog.map((c) => (
          <SelectItem key={c.id} value={c.id}>{c.name} — {c.category} · {c.unit}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="mb-6 flex flex-wrap gap-2" data-testid="stock-admin-tools">
      <Button variant="outline" data-testid="open-opening-stock-btn" onClick={() => setOpenOpening(true)}>
        <PackagePlus className="mr-2 h-4 w-4" /> Opening stock
      </Button>
      <Button variant="outline" data-testid="open-adjust-stock-btn" onClick={() => setOpenAdjust(true)}>
        <SlidersHorizontal className="mr-2 h-4 w-4" /> Adjust stock
      </Button>
      <Button variant="outline" data-testid="open-cleanup-btn" onClick={loadOrders}>
        <Eraser className="mr-2 h-4 w-4" /> Clean up delivery entries
      </Button>
      <Button
        variant="outline"
        data-testid="send-serials-wa-toolbar"
        onClick={() => sendSerialsWhatsApp(defaultStudioId)}
      >
        <MessageCircle className="mr-2 h-4 w-4" /> Send serial numbers via WhatsApp
      </Button>

      {/* ---- Opening stock ---- */}
      <Dialog open={openOpening} onOpenChange={setOpenOpening}>
        <DialogContent className="sm:max-w-lg" data-testid="opening-stock-dialog">
          <DialogHeader>
            <DialogTitle>Record opening stock</DialogTitle>
            <DialogDescription>
              Pre-system stock a studio already held. Marked as Opening Stock everywhere and kept out
              of sales/delivery reports.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div>
              <Label>Studio *</Label>
              {studioSelect(op.studio_id, (v) => setOp((s) => ({ ...s, studio_id: v })), "opening-studio-select")}
            </div>
            <div>
              <Label>Product *</Label>
              {skuSelect(op.catalog_sku_id, (v) => setOp((s) => ({ ...s, catalog_sku_id: v })), "opening-sku-select")}
            </div>
            {opIsRoll ? (
              <>
                <div>
                  <Label>Roll type</Label>
                  <Select value={op.roll_mode} onValueChange={(v) => setOp((s) => ({ ...s, roll_mode: v }))}>
                    <SelectTrigger data-testid="opening-roll-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">Full roll</SelectItem>
                      <SelectItem value="partial">Partial roll</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {op.roll_mode === "partial" && (
                  <div>
                    <Label>Remaining sqft on the roll *</Label>
                    <Input data-testid="opening-remaining-sqft" inputMode="decimal" value={op.remaining_sqft}
                      onChange={(e) => setOp((s) => ({ ...s, remaining_sqft: e.target.value }))} />
                  </div>
                )}
                {opSku?.category === "Sunfilm" && (
                  <div>
                    <Label>Roll capacity sqft (optional)</Label>
                    <Input data-testid="opening-capacity-sqft" inputMode="decimal" value={op.capacity_sqft}
                      onChange={(e) => setOp((s) => ({ ...s, capacity_sqft: e.target.value }))} />
                  </div>
                )}
                <p className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700" data-testid="opening-serial-note">
                  A unique serial number is generated automatically for this roll
                  {op.roll_mode === "partial" ? " and marked as a partial roll with its remaining sqft." : "."}
                </p>
              </>
            ) : (
              <div>
                <Label>Quantity ({opSku?.unit || "unit"}) *</Label>
                <Input data-testid="opening-quantity" inputMode="decimal" value={op.quantity}
                  onChange={(e) => setOp((s) => ({ ...s, quantity: e.target.value }))} />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date of entry</Label>
                <Input type="date" data-testid="opening-date" value={op.date}
                  onChange={(e) => setOp((s) => ({ ...s, date: e.target.value }))} />
              </div>
              <div>
                <Label>Unit cost (₹, optional)</Label>
                <Input data-testid="opening-unit-cost" inputMode="decimal" value={op.unit_cost}
                  onChange={(e) => setOp((s) => ({ ...s, unit_cost: e.target.value }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenOpening(false)}>Close</Button>
            <Button
              variant="outline"
              data-testid="send-serials-wa-dialog"
              onClick={() => sendSerialsWhatsApp(op.studio_id)}
              disabled={!op.studio_id}
            >
              <MessageCircle className="mr-2 h-4 w-4" /> Send to Studio via WhatsApp
            </Button>
            <Button data-testid="save-opening-stock-btn" onClick={submitOpening}
              disabled={!op.studio_id || !op.catalog_sku_id}>
              Record opening stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Adjustment ---- */}
      <Dialog open={openAdjust} onOpenChange={setOpenAdjust}>
        <DialogContent className="sm:max-w-lg" data-testid="adjust-stock-dialog">
          <DialogHeader>
            <DialogTitle>Adjust stock</DialogTitle>
            <DialogDescription>
              Correct on-hand quantity or a partial roll's remaining sqft. Every adjustment is logged
              with your name, role, reason and time.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div>
              <Label>Studio *</Label>
              {studioSelect(adj.studio_id, (v) => setAdj((s) => ({ ...s, studio_id: v })), "adjust-studio-select")}
            </div>
            <div>
              <Label>Product *</Label>
              {skuSelect(adj.catalog_sku_id, (v) => setAdj((s) => ({ ...s, catalog_sku_id: v })), "adjust-sku-select")}
            </div>
            <div>
              <Label>What are you correcting?</Label>
              <Select value={adj.mode} onValueChange={(v) => setAdj((s) => ({ ...s, mode: v }))}>
                <SelectTrigger data-testid="adjust-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="quantity">Quantity on hand</SelectItem>
                  <SelectItem value="roll">A specific roll's remaining sqft</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {adj.mode === "quantity" ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Direction</Label>
                  <Select value={adj.direction} onValueChange={(v) => setAdj((s) => ({ ...s, direction: v }))}>
                    <SelectTrigger data-testid="adjust-direction"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="add">Add (＋)</SelectItem>
                      <SelectItem value="remove">Remove (－)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Quantity ({adjSku?.unit || "unit"})</Label>
                  <Input data-testid="adjust-magnitude" inputMode="decimal" value={adj.magnitude}
                    onChange={(e) => setAdj((s) => ({ ...s, magnitude: e.target.value }))} />
                </div>
              </div>
            ) : (
              <>
                <div>
                  <Label>Roll serial *</Label>
                  <Select value={adj.serial_id} onValueChange={(v) => setAdj((s) => ({ ...s, serial_id: v }))}>
                    <SelectTrigger data-testid="adjust-serial-select"><SelectValue placeholder="Select a roll" /></SelectTrigger>
                    <SelectContent>
                      {serials.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.serial} — {r.balance_sqft} / {r.total_capacity} sqft left
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Actual remaining sqft *</Label>
                  <Input data-testid="adjust-actual-sqft" inputMode="decimal" value={adj.actual_remaining_sqft}
                    onChange={(e) => setAdj((s) => ({ ...s, actual_remaining_sqft: e.target.value }))} />
                </div>
              </>
            )}
            <div>
              <Label>Reason *</Label>
              <Select value={adj.reason} onValueChange={(v) => setAdj((s) => ({ ...s, reason: v }))}>
                <SelectTrigger data-testid="adjust-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>
                  {REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input data-testid="adjust-note" value={adj.note}
                onChange={(e) => setAdj((s) => ({ ...s, note: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenAdjust(false)}>Cancel</Button>
            <Button data-testid="save-adjust-stock-btn" onClick={submitAdjust}
              disabled={!adj.studio_id || !adj.catalog_sku_id || !adj.reason}>
              Apply adjustment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Cleanup delivery orders ---- */}
      <Dialog open={openCleanup} onOpenChange={setOpenCleanup}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-3xl" data-testid="cleanup-dialog">
          <DialogHeader>
            <DialogTitle>Studio delivery orders</DialogTitle>
            <DialogDescription>
              Delete backdated delivery orders that were wrongly used as opening stock. Deleting one
              reverses only its own stock and rolls — other records are untouched.
            </DialogDescription>
          </DialogHeader>
          <Table data-testid="cleanup-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Studio</TableHead>
                <TableHead>Items</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-slate-500">
                  No studio delivery orders found.
                </TableCell></TableRow>
              )}
              {orders.map((o) => (
                <TableRow key={o.id} data-testid={`cleanup-row-${o.id}`}>
                  <TableCell className="whitespace-nowrap">
                    {fmtDate(o.created_at)}
                    {o.looks_backdated && (
                      <Badge className="ml-2 border-0 bg-amber-50 text-amber-700">backdated</Badge>
                    )}
                  </TableCell>
                  <TableCell>{o.studio_name}</TableCell>
                  <TableCell className="text-slate-600">{o.items_summary}</TableCell>
                  <TableCell className="text-right">{money(o.revenue)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline"
                      className="border-rose-200 text-rose-600 hover:bg-rose-50"
                      data-testid={`cleanup-delete-${o.id}`} onClick={() => setDelOrder(o)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delOrder} onOpenChange={(o) => !o && setDelOrder(null)}>
        <DialogContent className="sm:max-w-md" data-testid="cleanup-confirm-dialog">
          <DialogHeader><DialogTitle>Delete this delivery order?</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Confirm reversing this delivery order.</DialogDescription>
          <p className="text-sm text-slate-600">
            {delOrder?.studio_name} · {delOrder?.items_summary}. This removes the order and reverses
            the stock it added (rolls it shipped are returned to the HQ pool). It's blocked if any of
            its rolls have already been used. This can't be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOrder(null)}>Cancel</Button>
            <Button className="bg-rose-600 text-white hover:bg-rose-700"
              data-testid="confirm-cleanup-delete-btn" onClick={confirmReverse}>
              Delete & reverse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
